import React from "react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Radio,
  Space,
  Typography,
} from "antd";
import { PRIORITIES, REPORTERS, STATUSES } from "../models/incidents";
import { UI_TIMESTAMP_FORMAT } from "../../../../utils/date.helpers";

import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const FiltersPanel = ({ filters, setFilters, tenant }) => {
  const { t } = useTranslation();
  const showReporterFilter = tenant === "galicia";

  const priorityOptions = Object.values(PRIORITIES).map((p) => ({
    label: <span className="text-sm text-navy-700 dark:text-white">{p}</span>,
    value: p,
  }));
  const statusOptions = Object.values(STATUSES).map((s) => ({
    label: <span className="text-sm text-navy-700 dark:text-white">{s}</span>,
    value: s,
  }));
  const reporterOptions = Object.values(REPORTERS).map((reporter) => ({
    label: (
      <span className="text-sm capitalize text-navy-700 dark:text-white">
        {reporter}
      </span>
    ),
    value: reporter,
  }));

  const clearAll = () => {
    setFilters({
      dateRange: [null, null],
      priorities: [],
      statuses: [],
      reporters: [],
      activeHistoric: "BOTH",
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col text-navy-700 dark:text-white">
      <div className="shrink-0 border-b border-gray-200 px-4 py-2 dark:border-white/20">
        <div className="text-base font-semibold text-navy-700 dark:text-white">
          {t("filters.title")}
        </div>
        <Text className="text-[12px] text-gray-500 dark:text-white/70">
          {t("filters.timestampFormat")} :{" "}
          <span className="font-semibold">{UI_TIMESTAMP_FORMAT}</span>
        </Text>
      </div>

      <div className="flex-1 overflow-auto px-2">
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Card
            size="small"
            title={
              <span className="text-sm font-semibold text-navy-700 dark:text-white">
                {t("filters.dateRange")}
              </span>
            }
            className="!border-gray-200 !bg-white dark:!border-white/20 dark:!bg-navy-800 dark:[&_.ant-card-head]:!border-white/20"
          >
            <RangePicker
              showTime={{ format: "HH:mm" }}
              format={UI_TIMESTAMP_FORMAT}
              className="!w-full !rounded-lg !border-gray-200 !bg-white !text-navy-700 dark:!border-white/40 dark:!bg-navy-900 dark:!text-white dark:[&_.ant-picker-input>input::placeholder]:!text-white/60 dark:[&_.ant-picker-input>input]:!text-white dark:[&_.ant-picker-separator]:!text-white/60"
              value={filters.dateRange}
              onChange={(range) =>
                setFilters((p) => ({ ...p, dateRange: range || [null, null] }))
              }
              allowClear
              placeholder={[t("filters.from"), t("filters.to")]}
              popupClassName="dark:[&_.ant-picker-panel-container]:!bg-white dark:[&_.ant-picker-panel-container]:!p-0"
            />
          </Card>

          <Card
            size="small"
            title={
              <span className="text-sm font-semibold text-navy-700 dark:text-white">
                {t("filters.priority")}
              </span>
            }
            className="!border-gray-200 !bg-white dark:!border-white/10 dark:!bg-navy-800 dark:[&_.ant-card-head]:!border-white/20"
          >
            <Checkbox.Group
              options={priorityOptions}
              value={filters.priorities}
              onChange={(vals) =>
                setFilters((p) => ({ ...p, priorities: vals }))
              }
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              className="text-navy-700 dark:text-white"
            />
          </Card>

          {showReporterFilter ? (
            <Card
              size="small"
              title={
                <span className="text-sm font-semibold text-navy-700 dark:text-white">
                  {t("filters.reporter")}
                </span>
              }
              className="!border-gray-200 !bg-white dark:!border-white/10 dark:!bg-navy-800 dark:[&_.ant-card-head]:!border-white/20"
            >
              <Checkbox.Group
                options={reporterOptions}
                value={filters.reporters}
                onChange={(vals) =>
                  setFilters((p) => ({ ...p, reporters: vals }))
                }
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
                className="text-navy-700 dark:text-white"
              />
            </Card>
          ) : null}

          <Card
            size="small"
            title={
              <span className="text-sm font-semibold text-navy-700 dark:text-white">
                {t("filters.status")}
              </span>
            }
            className="!border-gray-200 !bg-white dark:!border-white/10 dark:!bg-navy-800 dark:[&_.ant-card-head]:!border-white/20"
          >
            <Checkbox.Group
              options={statusOptions}
              value={filters.statuses}
              onChange={(vals) => setFilters((p) => ({ ...p, statuses: vals }))}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              className="text-navy-700 dark:text-white"
            />
          </Card>

          <Card
            size="small"
            title={
              <span className="text-sm font-semibold text-navy-700 dark:text-white">
                {t("filters.activeHistoric")}
              </span>
            }
            className="!border-gray-200 !bg-white dark:!border-white/10 dark:!bg-navy-800 dark:[&_.ant-card-head]:!border-white/20"
          >
            <Radio.Group
              value={filters.activeHistoric}
              onChange={(e) =>
                setFilters((p) => ({ ...p, activeHistoric: e.target.value }))
              }
            >
              <Space direction="vertical">
                <Radio value="ACTIVE" className="text-navy-700 dark:text-white">
                  {t("filters.active")}
                </Radio>
                <Radio
                  value="HISTORIC"
                  className="text-navy-700 dark:text-white"
                >
                  {t("filters.historic")}
                </Radio>
                <Radio value="BOTH" className="text-navy-700 dark:text-white">
                  {t("filters.both")}
                </Radio>
              </Space>
            </Radio.Group>

            <div className="mt-3">
              <Button
                onClick={clearAll}
                block
                className="!border-gray-200 !text-navy-700 hover:!border-gray-300 dark:!border-white/40 dark:!bg-navy-900 dark:!text-white dark:hover:!border-white/50"
              >
                {t("filters.clear")}
              </Button>
            </div>
          </Card>
        </Space>
      </div>
    </div>
  );
};

export default FiltersPanel;
